import {
  memo,
  useCallback,
  useState,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { Fab, Icon, colors, styled, Box } from '@mui/material';
import { css, keyframes } from '@emotion/react';
import { useRemoteDevices } from '@api/media';
import { useMount, useUnmount } from 'react-use';
import type { MediaClient } from '@tier4/webauto-media-client';

type ReceiveOperationProps = {
  vehicleName: string;
  telephone: MediaClient.RemoteTelephone;
};

const ReceiveOperation: React.FC<ReceiveOperationProps> = ({
  vehicleName,
  telephone,
}) => {
  const [telephoneState, setTelephoneState] =
    useState<MediaClient.RemoteTelephone.State>();

  const ringtone = useMemo(() => {
    const audio = new Audio('/assets/sound/ringtone.mp3');
    audio.loop = true;
    audio.muted = false;
    return audio;
  }, []);

  const handleClickReceive = useCallback(() => {
    try {
      telephone.answer();
    } catch (err) {
      console.log(err);
    }
  }, [telephone]);

  const handleClickHangUp = useCallback(() => {
    try {
      telephone.hangup();
    } catch (err) {
      console.log(err);
    }
  }, [telephone]);

  const handleChangeState = useCallback(() => {
    setTelephoneState(telephone.state);
  }, [telephone]);

  const isMountedRef = useRef(false);
  const isConnectedRef = useRef(false);

  const disconnect = useCallback(async () => {
    if (!isConnectedRef.current) return;
    isConnectedRef.current = false;
    telephone.removeStateListener(handleChangeState);
    // offline のとき「だけ」disconnect をスキップする。
    // ライブラリの disconnect() は state === 'offline' のときのみ例外を投げるため。
    // ringing / online / calling / talking / error など offline 以外の状態は
    // disconnect が有効なので、ここでは return せずそのまま切断する。
    if (telephone.state === 'offline') return;
    try {
      await telephone.disconnect();
    } catch (error) {
      console.warn('failed to disconnect telephone', error);
    }
  }, [telephone, handleChangeState]);

  useMount(async () => {
    isMountedRef.current = true;
    try {
      // 既に接続済みのデバイスに対して connect を呼ぶと例外になるため状態を確認する
      if (telephone.state === 'offline') {
        await telephone.connect();
      }
      isConnectedRef.current = true;
      telephone.addStateListener(handleChangeState);
      // connect 完了前にアンマウントされていた場合はここで切断する
      if (!isMountedRef.current) {
        await disconnect();
      }
    } catch (error) {
      console.warn('failed to connect telephone', error);
    }
  });

  useUnmount(() => {
    isMountedRef.current = false;
    void disconnect();
  });

  useEffect(() => {
    (async () => {
      if (telephoneState === 'ringing') {
        try {
          await ringtone.play();
        } catch (err) {
          console.log(err);
        }
      } else {
        ringtone.pause();
        ringtone.currentTime = 0;
      }
    })();
  }, [ringtone, telephoneState]);

  if (telephoneState !== 'ringing' && telephoneState !== 'talking')
    return <></>;

  return (
    <PopupOuter>
      <Wrapper state={telephoneState}>
        <CallWrapper className="call-wrapper">
          <Information className="call-info">
            <Label>
              <span>{telephone.tag}</span>
              <span>{telephoneState}</span>
            </Label>
            <VehicleName className="vehicle-name">{vehicleName}</VehicleName>
          </Information>
          <Nav>
            {telephoneState === 'ringing' && (
              <CallFab  onClick={handleClickReceive}>
                <Icon>call</Icon>
              </CallFab>
            )}
            {telephoneState === 'ringing' ? (
              <CloseFab onClick={handleClickHangUp}>
                <Icon>close</Icon>
              </CloseFab>
            ) : (
              <CallEndFab onClick={handleClickHangUp}>
                <Icon>call_end</Icon>
              </CallEndFab>
            )}
          </Nav>
        </CallWrapper>
      </Wrapper>
    </PopupOuter>
  );
};

type Props = {
  vehicleName: string;
  vehicleId: string;
};

const CallReceiverOver: React.FC<Props> = ({ vehicleId, vehicleName }) => {
  const { fetchDevices } = useRemoteDevices();
  const [telephones, setTelephones] = useState<MediaClient.RemoteTelephone[]>(
    [],
  );

  useMount(async () => {
    const devices = await fetchDevices(vehicleId);
    if (devices?.telephones.length) {
      setTelephones(devices.telephones);
    }
  });

  return (
    <>
      {telephones.map((telephone) => {
        return (
          <ReceiveOperation
            vehicleName={vehicleName}
            key={telephone.id}
            telephone={telephone}
          />
        );
      })}
    </>
  );
};

export default memo(CallReceiverOver);

const PopupOuter = styled(Box)`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(170, 170, 170, 0.2);
  z-index: 1500;
`;

type WrapperProps = {
  state: MediaClient.RemoteTelephone.State;
};

const Wrapper = styled('div')`
  margin-bottom: ${({ theme }) => theme.spacing(15)};
  ${({ state }: WrapperProps) => {
    if (state === 'talking') {
      return css`
        .call-wrapper {
          background-color: ${colors.green[500]};
        }
        .call-info {
          color: white;
        }
        .vehicle-name {
          color: white;
        }
      `;
    }
  }}
`;

const CallWrapper = styled('div')`
  width: 600px;
  height: 80px;
  display: flex;
  align-items: center;
  border-radius: 12px;
  background-color: white;
  box-sizing: border-box;
  padding: ${({ theme }) => theme.spacing(6)};
  font-family: Roboto;
  font-weight: 700;
`;
const Information = styled('div')`
  font-size: 14px;
  color: ${colors.blueGrey[400]};
  line-height: 1;
  flex: 1;
  padding-right: ${({ theme }) => theme.spacing(3)};
  overflow: hidden;
`;
const Label = styled('p')`
  margin: 0 0 6px;
  span {
    text-transform: capitalize;
    padding-right: 4px;
  }
`;
const VehicleName = styled('p')`
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 24px;
  color: ${colors.blueGrey[700]};
`;

const Nav = styled('div')`
  width: 104px;
  display: flex;
  justify-content: flex-end;
`;
const CustomFab = styled(Fab)`
  width: 42px;
  height: 42px;
  &.MuiFab-root {
    box-shadow: none;
    &:active {
      box-shadow: none;
    }
  }
  .MuiFab-label {
    color: white;
  }
  &:last-of-type {
    margin-left: ${({ theme }) => theme.spacing(5)};
  }
`;

const incomingAnimation = keyframes`
  0% {
    transform: scale(0.5);
    opacity: 0;
  }
  50% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.6);
    opacity: 0;
  }
`;

const CallFab = styled(CustomFab)`
  position: relative;
  &.MuiFab-root {
    background-color: ${colors.green[500]};
    &:hover {
      background-color: ${colors.green[700]};
    }
  }
  &::before,
  &::after {
    pointer-events: none;
    content: '';
    display: block;
    position: absolute;
    border-radius: 50%;
    width: 100%;
    height: 100%;
    border: solid 1px ${colors.green[500]};
  }
  &::before {
    animation: ${incomingAnimation} 1.6s linear infinite;
  }
  &::after {
    animation: ${incomingAnimation} 1.6s linear infinite;
    animation-delay: 0.4s;
  }
  &:hover {
    &::before,
    &::after {
      border-color: ${colors.green[700]};
    }
  }
`;

const CallEndFab = styled(CustomFab)`
  &.MuiFab-root {
    background-color: ${colors.red[500]};
    &:hover {
      background-color: ${colors.red[700]};
    }
  }
`;

const CloseFab = styled(CustomFab)`
  &.MuiFab-root {
    background-color: white;
    &:hover {
      background-color: ${colors.grey[100]};
    }
  }
  .MuiFab-label {
    color: ${colors.deepOrange[500]};
  }
`;
